import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert, FlatList, TouchableOpacity} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Dialog,
  Portal,
  Button,
  TextInput,
  Snackbar,
  FAB,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useAddOnStore} from '../../store/useAddOnStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const AddOnListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {addOns, isLoading, fetchAddOns, createAddOn, updateAddOn, deleteAddOn} =
    useAddOnStore();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [addonName, setAddonName] = useState('');
  const [addonPrice, setAddonPrice] = useState('');
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchAddOns();
  }, [fetchAddOns]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAddOns();
    setRefreshing(false);
  }, [fetchAddOns]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setAddonName('');
    setAddonPrice('');
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback(item => {
    setEditingItem(item);
    setAddonName(item.name);
    setAddonPrice(String(item.price));
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = addonName.trim();
    const price = parseFloat(addonPrice);
    if (!name || isNaN(price) || price < 0) return;

    try {
      if (editingItem) {
        await updateAddOn(editingItem.id, {name, price});
      } else {
        await createAddOn({name, price});
      }
      setDialogVisible(false);
      setAddonName('');
      setAddonPrice('');
      setEditingItem(null);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [addonName, addonPrice, editingItem, createAddOn, updateAddOn, t]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(
        t('common.delete'),
        t('addon.deleteConfirm', {name: item.name}),
        [
          {text: t('common.cancel'), style: 'cancel'},
          {
            text: t('common.delete'),
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteAddOn(item.id);
              } catch (e) {
                setSnackbar(e.response?.data?.error?.message || t('common.error'));
              }
            },
          },
        ],
      );
    },
    [deleteAddOn, t],
  );

  const renderItem = useCallback(
    ({item}) => (
      <View style={styles.card}>
        <View style={styles.cardRow}>
          <View style={styles.iconCircle}>
            <Icon name="puzzle-outline" size={18} color={colors.primary} />
          </View>
          <View style={styles.cardInfo}>
            <Text style={styles.itemName}>{item.name}</Text>
            <Text style={styles.itemPrice}>{'\u20B9'}{Number(item.price).toLocaleString()}</Text>
          </View>
          <TouchableOpacity
            onPress={() => openEditDialog(item)}
            hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
            style={styles.actionBtn}>
            <Icon name="pencil-outline" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => handleDelete(item)}
            hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
            style={styles.actionBtn}>
            <Icon name="delete-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        </View>
      </View>
    ),
    [openEditDialog, handleDelete],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View style={styles.descriptionRow}>
        <Icon name="information-outline" size={16} color={colors.textSecondary} />
        <Text style={styles.descriptionText}>{t('addon.description')}</Text>
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => (
      <View style={styles.emptyContainer}>
        <Icon name="puzzle-outline" size={48} color={colors.textLight} />
        <Text style={styles.emptyText}>{t('common.noData')}</Text>
        <Text style={styles.emptySubtext}>Tap + to add your first add-on</Text>
      </View>
    ),
    [t],
  );

  if (isLoading && addOns.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('addon.title')} onBack={() => navigation.goBack()} />

      <FlatList
        data={addOns}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        contentContainerStyle={styles.listContent}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? t('addon.updateAddon') : t('addon.addAddon')}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('addon.name')}
              value={addonName}
              onChangeText={setAddonName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label={t('addon.price')}
              value={addonPrice}
              onChangeText={setAddonPrice}
              mode="outlined"
              keyboardType="numeric"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>{t('common.cancel')}</Button>
            <Button mode="contained" onPress={handleSave} style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB icon="plus" style={styles.fab} onPress={openAddDialog} color={colors.white} />

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.md, paddingBottom: 80},

  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.primary + '08',
    padding: spacing.md,
    borderRadius: 10,
    marginBottom: spacing.md,
  },
  descriptionText: {
    flex: 1,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary + '12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardInfo: {flex: 1},
  itemName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  itemPrice: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.success,
    marginTop: 1,
  },
  actionBtn: {
    padding: 6,
  },

  emptyContainer: {alignItems: 'center', paddingTop: 60},
  emptyText: {fontSize: 16, fontWeight: '600', color: colors.text, marginTop: spacing.md},
  emptySubtext: {fontSize: 13, color: colors.textSecondary, marginTop: spacing.xs},

  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default AddOnListScreen;
